import { Tag, Flex } from "antd";
import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import TrialIcon from "../../assets/icons/book.svg?react";
import LinkIcon from "../../assets/icons/link.svg?react";
import type { ReferenceDocs, ProtocolSource } from "./chat";
import ExplanationCard from "./ExplanationCard";
import { useFetchBlobUrl } from "@/hooks/useFetchBlobUrl";

type GroupedDoc = {
  source_document_name: string;
  source_url: string;
  page_spans: { first_page: number; last_page: number }[];
};

function groupDocsByName(docs: ReferenceDocs[]): GroupedDoc[] {
  const map = new Map<string, GroupedDoc>();
  for (const doc of docs) {
    const existing = map.get(doc.source_document_name);
    if (existing) {
      existing.page_spans.push(doc.page_span);
    } else {
      map.set(doc.source_document_name, {
        source_document_name: doc.source_document_name,
        source_url: doc.source_url,
        page_spans: [doc.page_span],
      });
    }
  }
  return Array.from(map.values());
}

function PageSpanTag({
  span,
  sourceUrl,
}: {
  span: GroupedDoc["page_spans"][number];
  sourceUrl: string | undefined;
}) {
  const { createBlobUrlAsync } = useFetchBlobUrl();

  const handleClick = async () => {
    if (!sourceUrl) return;
    const newTab = window.open("about:blank", "_blank");
    try {
      const blobUrl = await createBlobUrlAsync(sourceUrl);
      if (newTab) {
        newTab.location.href = `${blobUrl}#page=${span.first_page}`;
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      } else {
        URL.revokeObjectURL(blobUrl);
      }
    } catch {
      newTab?.close();
    }
  };

  return (
    <Tag
      className={`border-[#66928E] bg-[#F0FAF7] rounded-[100px] p-[2px_8px_2px_6px] w-fit ${sourceUrl ? "cursor-pointer hover:bg-[#d9f0ea]" : "cursor-default"}`}
      onClick={handleClick}
    >
      <div className="flex items-center gap-1 text-[#336E68] font-medium">
        <LinkIcon />
        <p>Pages</p>
        {span.first_page} - {span.last_page}
      </div>
    </Tag>
  );
}

function ProtocolCollapseChildren({
  response,
  docs,
}: {
  response: string;
  docs: ReferenceDocs[];
}) {
  return (
    <div className="flex flex-col">
      <div className="markdown text-[#2B4752]">
        <MarkdownRenderer content={response} />
      </div>
      <p className="text-base font-medium mt-2 mb-2">REFERENCES</p>
      {groupDocsByName(docs).map((d) => (
        <div
          className="flex flex-col gap-2 mb-2 text-[#2B4752]"
          key={d.source_document_name}
        >
          <p>{d.source_document_name}</p>
          <Flex wrap gap="small">
            {d.page_spans.map((span) => (
              <PageSpanTag
                key={`${span.first_page}-${span.last_page}`}
                span={span}
                sourceUrl={d.source_url}
              />
            ))}
          </Flex>
        </div>
      ))}
    </div>
  );
}

export default function ChatProtocolCard({
  sources,
  chatExplanation = true,
}: {
  sources: ProtocolSource[];
  chatExplanation?: boolean;
}) {
  return (
    <div data-testid="trial-protocol-agent-section">
      {sources.map((s, index) => (
        <div
          className="flex flex-col"
          key={s.citation?.reference_docs?.[0]?.source_url ?? index}
        >
          <ExplanationCard
            title="Trial Protocol Agent"
            Icon={TrialIcon}
            iconColor="text-[#4BA06E]"
            chatExplanation={chatExplanation}
          >
            <ProtocolCollapseChildren
              response={s.response}
              docs={s.citation?.reference_docs ?? []}
            />
          </ExplanationCard>
        </div>
      ))}
    </div>
  );
}
